import React from 'react';
import { Link } from 'react-router-dom';
import { Film, Tv, CalendarClock, Zap, TrendingUp } from 'lucide-react';

// Shared with IntelligentResults.js (AI search results) and IntelligenceFeed.js
// (homepage) — both render the same release/event/trend graph items, so the
// card markup lives here once rather than twice.

export const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? '' : dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

export function MediaIcon({ mediaType, className }) {
  return mediaType === 'tv' ? <Tv className={className} /> : <Film className={className} />;
}

// The title node a card points at, if any (so a caller can follow/watchlist it).
export function cardProjectId(r) {
  return r.project_id || r.entity_id || null;
}

// Prefer the public movie/TV detail page (works for everyone, rich data) when a
// TMDb id exists; fall back to the Insider project page for in-development nodes.
export function detailLink(r) {
  if (r.tmdb_id) return `/${r.media_type === 'tv' ? 'tv' : 'movie'}/${r.tmdb_id}`;
  // A game has no TMDb id and its own public page, so it must not fall through
  // to the gated Insider view.
  if (r.media_type === 'game') return `/game/${cardProjectId(r) || r.id}`;
  return `/insider/project/${cardProjectId(r) || r.id}`;
}

// Renders one release/event/trend graph item as a card. `follow` is an
// optional node overlaid top-right (e.g. a watchlist toggle) — omit it for a
// read-only context like the homepage feed.
export default function FeedCard({ r, follow = null }) {
  switch (r.type) {
    case 'release':
      return (
        <Link to={detailLink(r)} className="relative block bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow">
          {follow}
          <div className="flex items-center gap-2 text-xs text-videbits-600 mb-1"><CalendarClock className="w-3.5 h-3.5" /> {fmtDate(r.date)}</div>
          <div className="font-medium text-gray-900 flex items-center gap-1.5 pr-20"><MediaIcon mediaType={r.media_type} className="w-3.5 h-3.5 text-gray-400" />{r.title}</div>
          <div className="text-xs text-gray-500 mt-1">{(r.event_type || '').replace(/_/g, ' ').toLowerCase()}</div>
        </Link>
      );
    case 'event':
      return (
        <Link to={detailLink(r)} className="relative block bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow">
          {follow}
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-1"><Zap className="w-3.5 h-3.5" /> {fmtDate(r.date)}</div>
          <div className="font-medium text-gray-900 pr-20">{r.title}</div>
          <div className="text-xs text-gray-500 mt-1">{(r.event_type || '').replace(/_/g, ' ').toLowerCase()}</div>
        </Link>
      );
    case 'trend':
      return (
        <Link to={detailLink(r)} className="relative block bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow">
          {follow}
          <div className="flex items-center gap-2 text-xs text-green-700 mb-1"><TrendingUp className="w-3.5 h-3.5" /> {(r.trend_type || '').replace(/_/g, ' ')}{typeof r.percentage_change === 'number' ? ` · +${Math.round(r.percentage_change)}%` : ''}</div>
          <div className="font-medium text-gray-900 pr-20">{r.entity}</div>
          <div className="text-xs text-gray-500 mt-1">{r.reason}</div>
        </Link>
      );
    default:
      return null;
  }
}
